import * as React from 'react';
import { Stack, Typography, Box, Button, Divider } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Form, FileInput, FileField, useNotify, useRefresh } from 'react-admin';
import { apiUrl, httpClient } from "config/dataProvider";
import Modal from "components/Modal";

export type ImportReportType = 'info' | 'error';
export type ImportType = 'string' | 'date' | 'number' | 'bool';

export type ImportHeader = {
  header: string;
  required: boolean;
  type: ImportType;
};

export type ImportReport = {
  type: ImportReportType;
  header: ImportHeader;
  row: number;
  column: number;
  message: string;
};

export type ImportPreviewReport = {
  ok: boolean;
  reports: ImportReport[];
};

export type ImportResultReport = {
  ok: boolean;
};

async function previewQuestionsImport(file: File): Promise<ImportPreviewReport | null> {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await httpClient(
      `${apiUrl}question/import/preview`,
      { method: 'POST', body: formData },
    );
    return response.json as ImportPreviewReport;
  } catch {
    return null;
  }
}

async function importQuestions(file: File): Promise<ImportResultReport | null> {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await httpClient(
      `${apiUrl}question/import/apply`,
      { method: 'POST', body: formData },
    );
    return response.json as ImportResultReport;
  } catch {
    return null;
  }
}

export type QuestionImportExcelModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const QuestionImportExcelModal = ({ isOpen, onClose }: QuestionImportExcelModalProps) => {
  const notify = useNotify();
  const refresh = useRefresh();

  const [preview, setPreview] = React.useState<ImportPreviewReport | null>(null);

  const [isLoading, setIsLoading] = React.useState(false);

  const handleClose = () => {
    setPreview(null);
    onClose();
  };

  const onSubmit = async (data: any) => {
    const rawFile: File | undefined = data?.file?.rawFile;
    if (!rawFile) {
      notify('Выберите файл', { type: 'warning' });
      return;
    }

    setIsLoading(true);
    try {
      if (preview) {
        const resultReport = await importQuestions(rawFile);
        if (!resultReport) {
          notify('Ошибка загрузки файла', { type: 'error' });
          return;
        }
        notify('Вопросы импортированы', { type: 'success' });
        refresh();
        handleClose();
      } else {
        const previewReport = await previewQuestionsImport(rawFile);
        if (!previewReport) {
          notify('Ошибка загрузки файла', { type: 'error' });
          return;
        }
        setPreview(previewReport);
        notify('Отчет загружен', { type: 'success' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <Form onSubmit={onSubmit}>
        <Stack spacing={2}>
          <Typography variant="h6">Импорт вопросов из Excel</Typography>

          <FileInput
            fullWidth
            source="file"
            label="XLSX файл"
            multiple={false}
          >
            <FileField source="src" title="title"/>
          </FileInput>

          <Box display="flex" justifyContent="flex-end" gap={1}>
            <Button onClick={handleClose}>Отмена</Button>

            <LoadingButton
              loading={isLoading}
              variant="contained"
              type="submit"
            >
              {preview?.ok ? 'Импортировать' : 'Загрузить'}
            </LoadingButton>
          </Box>

          {preview && (
            <>
              <Divider/>
              <Typography
                sx={{ color: preview.ok ? "green" : 'red' }}
                variant="subtitle1">
                {preview.ok ? 'Отчет:' : 'Отчет (содержит ошибки):'}
              </Typography>

              <Stack spacing={1} sx={{ maxHeight: 360, overflow: 'auto' }}>
                {
                  preview.reports.map((r, id) => (
                    <Box
                      key={id}
                      sx={{ p: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                      <Typography
                        variant="body2"
                        sx={{ color: preview.ok ? "green" : 'red', fontWeight: 600 }}>
                        {r.header && r.column && r.row ? `${r.header?.header} [${r.column},${r.row}]` : 'Информация'}
                      </Typography>
                      <Typography
                        sx={{ color: preview.ok ? "green" : 'red' }}
                        variant="body2">
                        {r.message}
                      </Typography>
                    </Box>
                  ))
                }
              </Stack>
            </>
          )}
        </Stack>
      </Form>
    </Modal>
  );
};

export default QuestionImportExcelModal;
